import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';
import { ArrowLeft, Download } from 'lucide-react';
import RatingControl from '../components/RatingControl';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const TeamAppraisals = () => {
  const { user } = useAuth();
  const [appraisals, setAppraisals] = useState([]);
  const [selectedAppraisal, setSelectedAppraisal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloadingPdf, setDownloadingPdf] = useState(false);

  useEffect(() => {
    fetchAppraisals();
  }, []);

  const fetchAppraisals = async () => {
    try {
      const response = await axios.get(`${API_URL}/appraisals`);
      const teamAppraisals = response.data.filter(a => 
        a.manager_id === user.id && a.employee_id !== user.id
      );
      setAppraisals(teamAppraisals);
    } catch (error) {
      toast.error('Failed to load team appraisals');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveProgress = async () => {
    try {
      await axios.put(`${API_URL}/appraisals/${selectedAppraisal.id}`, {
        ...selectedAppraisal,
        status: 'IN_MANAGER_REVIEW'
      });
      toast.success('Progress saved successfully');
      fetchAppraisals();
    } catch (error) {
      toast.error('Failed to save progress');
    }
  };

  const handleSendToSecondLevel = async () => {
    try {
      await axios.put(`${API_URL}/appraisals/${selectedAppraisal.id}`, {
        ...selectedAppraisal,
        status: 'SECOND_LEVEL_REVIEW'
      });
      toast.success('Sent for 2nd-level review successfully');
      setSelectedAppraisal(null);
      fetchAppraisals();
    } catch (error) {
      toast.error('Failed to send for 2nd-level review');
    }
  };
  
  const handleDownloadPdf = async () => {
    setDownloadingPdf(true);
    try {
      const response = await axios.get(
        `${API_URL}/appraisals/${selectedAppraisal.id}/pdf`,
        { responseType: 'blob' }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Appraisal_${selectedAppraisal.employee_name.replace(/\s/g, '_')}_${selectedAppraisal.cycle_name.replace(/\s/g, '_')}_${selectedAppraisal.cycle_year}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('PDF downloaded successfully');
    } catch (error) {
      toast.error('Failed to download PDF');
    } finally {
      setDownloadingPdf(false);
    }
  };

  const getStatusBadge = (status) => {
    const labels = {
      'DRAFT_SELF': 'Draft',
      'SUBMITTED_SELF': 'Pending Manager',
      'IN_MANAGER_REVIEW': 'Manager Review',
      'SECOND_LEVEL_REVIEW': 'Calibration',
      'FINALIZED': 'Finalized'
    };

    return (
      <Badge variant="outline" className="uppercase tracking-wider text-xs border border-slate-200" data-testid={`status-${status}`}>
        {labels[status]}
      </Badge>
    );
  };

  if (loading) return <div className="text-sm text-slate-700">Loading...</div>;

  if (selectedAppraisal) {
    const isEditable = selectedAppraisal.status === 'SUBMITTED_SELF' || selectedAppraisal.status === 'IN_MANAGER_REVIEW';

    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl tracking-tight font-semibold text-slate-900" data-testid="manager-review-title">Manager Review</h1>
            <p className="text-sm text-slate-700 mt-2">{selectedAppraisal.employee_name} - {selectedAppraisal.cycle_name} {selectedAppraisal.cycle_year}</p>
          </div>
          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={handleDownloadPdf}
              disabled={downloadingPdf}
              className="border-slate-200 rounded-sm"
              data-testid="download-pdf-button"
            >
              <Download className="mr-2 h-4 w-4" />
              {downloadingPdf ? 'Downloading...' : 'Download PDF'}
            </Button>
            <Button
              variant="outline"
              onClick={() => setSelectedAppraisal(null)}
              className="border-slate-200 rounded-sm"
              data-testid="back-button"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to List
            </Button>
          </div>
        </div>

        <Card className="border border-slate-200 bg-white rounded-sm p-6 space-y-6">
          {/* Employee Self-Assessment (Read-only) */}
          <div>
            <h3 className="text-xl font-medium text-slate-900 mb-4">Employee Self-Assessment</h3>
            <div className="space-y-6">
              {selectedAppraisal.competencies.map((comp, idx) => (
                <div key={idx} className="border border-slate-200 bg-slate-50 p-4 rounded-sm" data-testid={`competency-${idx}`}>
                  <div className="font-semibold text-slate-900 mb-3">{comp.name}</div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div>
                      <div className="text-xs text-slate-500 mb-1">Employee Rating</div>
                      <div className="text-slate-900 font-semibold">{comp.self_rating || '-'}</div>
                    </div>
                    <div>
                      <div className="text-xs text-slate-500 mb-1">Employee Comments</div>
                      <div className="text-sm text-slate-700">{comp.self_comment || '-'}</div>
                    </div>
                  </div>

                  {/* Manager Review */}
                  <div className="pt-4 border-t border-slate-200">
                    <div className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500 mb-3">Your Manager Review</div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500 mb-2 block">Your Rating</Label>
                        {isEditable ? (
                          <RatingControl
                            value={comp.manager_rating}
                            onChange={(rating) => {
                              const updated = { ...selectedAppraisal };
                              updated.competencies[idx].manager_rating = rating;
                              setSelectedAppraisal(updated);
                            }}
                            testId={`manager-rating-${idx}`}
                          />
                        ) : (
                          <div className="text-slate-900 font-semibold">{comp.manager_rating || '-'}</div>
                        )}
                      </div>
                      
                      <div>
                        <Label className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500 mb-2 block">Your Comments</Label>
                        {isEditable ? (
                          <Textarea
                            value={comp.manager_comment || ''}
                            onChange={(e) => {
                              const updated = { ...selectedAppraisal };
                              updated.competencies[idx].manager_comment = e.target.value;
                              setSelectedAppraisal(updated);
                            }}
                            className="border-slate-200 rounded-sm min-h-[80px]"
                            data-testid={`manager-comment-${idx}`}
                          />
                        ) : (
                          <div className="text-sm text-slate-700">{comp.manager_comment || '-'}</div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Manager Overall Rating & Summary */}
          <div className="border-t border-slate-200 pt-6 space-y-4">
            <h3 className="text-xl font-medium text-slate-900">Overall Manager Assessment</h3>
            
            <div>
              <Label className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500 mb-2 block">Overall Manager Rating (1-5)</Label>
              {isEditable ? (
                <RatingControl
                  value={selectedAppraisal.manager_overall_rating}
                  onChange={(rating) => setSelectedAppraisal({ ...selectedAppraisal, manager_overall_rating: rating })}
                  testId="manager-overall-rating"
                />
              ) : (
                <div className="text-slate-900 font-semibold">{selectedAppraisal.manager_overall_rating || '-'}</div>
              )}
            </div>

            <div>
              <Label htmlFor="manager-summary" className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500 mb-2 block">Manager Summary</Label>
              {isEditable ? (
                <Textarea
                  id="manager-summary"
                  value={selectedAppraisal.manager_summary || ''}
                  onChange={(e) => setSelectedAppraisal({ ...selectedAppraisal, manager_summary: e.target.value })}
                  className="border-slate-200 rounded-sm min-h-[100px]"
                  data-testid="manager-summary-input"
                />
              ) : (
                <div className="text-sm text-slate-700">{selectedAppraisal.manager_summary || '-'}</div>
              )}
            </div>

            <div>
              <Label htmlFor="development-actions" className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500 mb-2 block">Development Actions</Label>
              {isEditable ? (
                <Textarea
                  id="development-actions"
                  value={selectedAppraisal.manager_development_actions || ''}
                  onChange={(e) => setSelectedAppraisal({ ...selectedAppraisal, manager_development_actions: e.target.value })}
                  className="border-slate-200 rounded-sm min-h-[100px]"
                  data-testid="development-actions-input"
                />
              ) : (
                <div className="text-sm text-slate-700">{selectedAppraisal.manager_development_actions || '-'}</div>
              )}
            </div>
          </div>

          {/* Action Buttons */}
          {isEditable && (
            <div className="flex gap-4 border-t border-slate-200 pt-6">
              <Button
                onClick={handleSaveProgress}
                variant="outline"
                className="border-slate-200 rounded-sm"
                data-testid="save-progress-button"
              >
                Save Progress
              </Button>
              <Button
                onClick={handleSendToSecondLevel}
                className="bg-[#1E40AF] hover:bg-[#1E3A8A] text-white rounded-sm"
                data-testid="send-to-second-level-button"
              >
                Send for 2nd-Level Review
              </Button>
            </div>
          )}
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-4xl tracking-tight font-semibold text-slate-900" data-testid="team-appraisals-title">Team Appraisals</h1>
        <p className="text-sm text-slate-700 mt-2">Review self-assessments from direct reports, then send them to your manager for calibration.</p>
      </div>

      <Card className="border border-slate-200 bg-white rounded-sm">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="team-appraisals-table">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left p-4 text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Employee</th>
                <th className="text-left p-4 text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Cycle</th>
                <th className="text-left p-4 text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Status</th>
                <th className="text-left p-4 text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Self Submitted</th>
                <th className="text-left p-4 text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Self Rating</th>
                <th className="text-left p-4 text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Manager Rating</th>
                <th className="text-left p-4 text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Updated</th>
                <th className="text-left p-4 text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Action</th>
              </tr>
            </thead>
            <tbody>
              {appraisals.length === 0 ? (
                <tr>
                  <td colSpan="8" className="text-center p-8 text-slate-500">No team appraisals found</td>
                </tr>
              ) : (
                appraisals.map((appraisal) => (
                  <tr key={appraisal.id} className="border-b border-slate-200 hover:bg-slate-50 transition-colors duration-200" data-testid={`team-appraisal-row-${appraisal.id}`}>
                    <td className="p-4 text-slate-700">{appraisal.employee_name}</td>
                    <td className="p-4 text-slate-700">{appraisal.cycle_name} {appraisal.cycle_year}</td>
                    <td className="p-4">{getStatusBadge(appraisal.status)}</td>
                    <td className="p-4 text-slate-500">
                      {appraisal.submitted_self_at ? formatDistanceToNow(new Date(appraisal.submitted_self_at), { addSuffix: true }) : '-'}
                    </td>
                    <td className="p-4 text-slate-900 font-semibold">{appraisal.self_overall_rating || '-'}</td>
                    <td className="p-4 text-slate-900 font-semibold">{appraisal.manager_overall_rating || '-'}</td>
                    <td className="p-4 text-slate-500">{formatDistanceToNow(new Date(appraisal.updated_at), { addSuffix: true })}</td>
                    <td className="p-4">
                      <Button
                        onClick={() => setSelectedAppraisal(appraisal)}
                        variant="outline"
                        size="sm"
                        className="border-slate-200 rounded-sm"
                        data-testid={`review-appraisal-${appraisal.id}`}
                      >
                        Review
                      </Button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
};

export default TeamAppraisals;
